// src/lib/placeholders.ts
// Función para generar placeholders SVG dinámicos

export const generatePlaceholderSVG = (
    width: number = 300,
    height: number = 300,
    text: string = '',
    bgColor: string = '#f3f4f6',
    textColor: string = '#9ca3af',
    iconType: 'image' | 'user' | 'category' | 'product' = 'image'
): string => {

    // Diferentes iconos SVG según el tipo
    const icons = {
        image: `
        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        `,
        user: `
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="12" cy="7" r="4" stroke="${textColor}" stroke-width="2" fill="none"/>
        `,
        category: `
        <rect x="3" y="3" width="7" height="7" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="14" y="3" width="7" height="7" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="14" y="14" width="7" height="7" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="3" y="14" width="7" height="7" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        `,
        product: `
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="3" y1="6" x2="21" y2="6" stroke="${textColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16 10a4 4 0 01-8 0" stroke="${textColor}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    `
    };

    //const iconSize = Math.min(width, height) * 0.3;
    const iconX = (width - 24) / 2;
    const iconY = text ? (height - 40) / 2 : (height - 24) / 2;
    const textY = iconY + 35;

    const svg = `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${bgColor}"/>
        <g transform="translate(${iconX}, ${iconY})">
            ${icons[iconType]}
        </g>
        ${text ? `
            <text x="50%" y="${textY}" font-family="system-ui, -apple-system, sans-serif" 
                font-size="12" font-weight="500" fill="${textColor}" text-anchor="middle" dy=".3em">
            ${text}
            </text>
        ` : ''}
        </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Placeholders predefinidos que se generan una vez al importar el módulo
export const placeholders = {
    // Productos
    product: generatePlaceholderSVG(300, 300, 'Producto', '#f3f4f6', '#9ca3af', 'product'),

    // Categorías (iconos pequeños)
    category: generatePlaceholderSVG(64, 64, '', '#f3f4f6', '#9ca3af', 'category'),

    // Usuario/Avatar
    user: generatePlaceholderSVG(40, 40, '', '#f3f4f6', '#9ca3af', 'user'),
    userLarge: generatePlaceholderSVG(80, 80, '', '#f3f4f6', '#9ca3af', 'user'),

    // General/Hero images
    general: generatePlaceholderSVG(400, 300, 'Imagen no disponible', '#f8fafc', '#94a3b8', 'image'),
    hero: generatePlaceholderSVG(800, 400, '', '#f1f5f9', '#64748b', 'image'),

    // Específicos por tamaño
    small: generatePlaceholderSVG(150, 150, '', '#f3f4f6', '#9ca3af', 'image'),
    medium: generatePlaceholderSVG(300, 300, '', '#f3f4f6', '#9ca3af', 'image'),
    large: generatePlaceholderSVG(600, 400, '', '#f3f4f6', '#9ca3af', 'image'),

    // Estados específicos
    loading: generatePlaceholderSVG(300, 300, 'Cargando...', '#eff6ff', '#3b82f6', 'image'),
    error: generatePlaceholderSVG(300, 300, 'Error al cargar', '#fef2f2', '#ef4444', 'image'),
    noImage: generatePlaceholderSVG(300, 300, 'Sin imagen', '#f9fafb', '#6b7280', 'image'),
};

// Hook para usar placeholders dinámicos
export const usePlaceholder = (
    type: keyof typeof placeholders = 'general',
    customOptions?: {
        width?: number;
        height?: number;
        text?: string;
        bgColor?: string;
        textColor?: string;
        iconType?: 'image' | 'user' | 'category' | 'product';
    }
) => {
    if (customOptions) {
        return generatePlaceholderSVG(
            customOptions.width,
            customOptions.height,
            customOptions.text,
            customOptions.bgColor,
            customOptions.textColor,
            customOptions.iconType
        );
    }

    return placeholders[type];
};

// Función para obtener placeholder por dimensiones
export const getPlaceholderBySize = (width: number, height: number, text?: string) => {
    return generatePlaceholderSVG(width, height, text);
};

// Función para obtener placeholder por contexto
export const getContextualPlaceholder = (context: 'product' | 'category' | 'user' | 'hero') => {
    const configs = {
        product: { width: 300, height: 300, text: 'Producto', iconType: 'product' as const },
        category: { width: 64, height: 64, text: '', iconType: 'category' as const },
        user: { width: 40, height: 40, text: '', iconType: 'user' as const },
        hero: { width: 800, height: 400, text: '', iconType: 'image' as const }
    };

    const config = configs[context];
    return generatePlaceholderSVG(
        config.width,
        config.height,
        config.text,
        '#f3f4f6',
        '#9ca3af',
        config.iconType
    );
};